import React, { useEffect } from 'react';
import errorStyles from '../../assets/css/error.module.css';
import { WithChildren } from '../../types/utility';
import { selectError } from '../../app/slices/error.slice';
import { useAppSelector } from '../../app/hooks';
import { AppError } from '../../types/error';
import { TechnicalErrorIcon } from '../svgs/TechnicalErrorIcon';
import { useTranslation } from 'react-i18next';
import { callbackRegistry } from '../../features/session/callback-registry';
import useCustomization from '../../hooks/useCustomization';

export const AppErrorBoundary = ({ children }: WithChildren) => {
	const error = useAppSelector(selectError);
	if (error.raised) return <AppErrorFallbackComponent error={error} />;
	return <React.Fragment>{children}</React.Fragment>;
};

interface AppErrorProps {
	error: AppError;
}

export const AppErrorFallbackComponent = ({ error }: AppErrorProps) => {
	const title = useCustomization('TECHNICAL_ERROR_PAGE_TITLE');
	const info = useCustomization('TECHNICAL_ERROR_PAGE_INFO');
	const additionalInformation = useCustomization('TECHNICAL_ERROR_PAGE_ADDITIONAL_INFORMATION');
	const { t } = useTranslation();

	useEffect(() => {
		callbackRegistry.retrieve('onError')(error.reason);
	}, [error]);

	return (
		<div className={errorStyles['error-container']}>
			<div className={errorStyles['error-content']}>
				<div className={errorStyles['error-icon']}>
					<TechnicalErrorIcon key="technical-error-icon" />
				</div>
				<div className={errorStyles['error-title']} aria-label="error=title">
					{title}
				</div>
				{info && (
					<div className={errorStyles['error-text-container']}>
						<p className={errorStyles['error-text']} aria-label="error-message">
							{info}
						</p>
					</div>
				)}
				{additionalInformation && (
					<div className={errorStyles['error-text-container']}>
						<p className={errorStyles['error-text']} aria-label="error-message">
							{additionalInformation}
						</p>
					</div>
				)}
				<div className={errorStyles['error-text-container']}>
					{error.reference && (
						<p className={errorStyles['error-text']} aria-label="error-message">
							{t('error-boundary.reference', { reference: error.reference })}
						</p>
					)}
					{!error.reference && error.traceId && (
						<p className={errorStyles['error-text']} aria-label="error-message">
							{t('error-boundary.reference', { reference: error.traceId })}
						</p>
					)}
				</div>
			</div>
		</div>
	);
};
